(function () {
  /**
   * @param {string} url
   * @returns {{ host: 'youtube' | 'vimeo', id: string } | null}
   */
  function parseEmbedVideoUrl(url) {
    if (typeof url !== 'string' || !url.trim()) return null;

    const normalized = url.trim();

    const youtubePatterns = [
      /(?:youtube\.com\/watch\?(?:.*&)?v=|youtube\.com\/watch\/|youtube\.com\/embed\/|youtube\.com\/shorts\/|youtube\.com\/live\/|youtube\.com\/v\/|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/.*[?&]v=([a-zA-Z0-9_-]{11})/,
    ];

    for (const pattern of youtubePatterns) {
      const match = normalized.match(pattern);
      if (match && match[1]) {
        return { host: 'youtube', id: match[1] };
      }
    }

    const vimeoMatch = normalized.match(/(?:vimeo\.com\/(?:video\/)?|player\.vimeo\.com\/video\/)(\d+)/);
    if (vimeoMatch && vimeoMatch[1]) {
      return { host: 'vimeo', id: vimeoMatch[1] };
    }

    return null;
  }

  /**
   * @param {HTMLImageElement} image
   * @param {string} videoId
   */
  function setYouTubeThumbImage(image, videoId) {
    const sources = [
      `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`,
      `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`,
      `https://i.ytimg.com/vi/${videoId}/sddefault.jpg`,
    ];

    let sourceIndex = 0;

    image.onerror = () => {
      sourceIndex += 1;
      const nextSource = sources[sourceIndex];
      if (nextSource) {
        image.src = nextSource;
      }
    };

    const initialSource = sources[0];
    if (initialSource) {
      image.src = initialSource;
    }
  }

  /**
   * @param {HTMLImageElement} image
   * @param {string} videoId
   */
  function setVimeoThumbImage(image, videoId) {
    image.onerror = null;
    image.src = `https://vumbnail.com/${videoId}.jpg`;
  }

  /**
   * @param {'youtube' | 'vimeo'} host
   * @param {string} id
   * @returns {string}
   */
  function buildEmbedSrc(host, id) {
    const origin = encodeURIComponent(window.location.origin);

    if (host === 'youtube') {
      return `https://www.youtube-nocookie.com/embed/${id}?rel=0&modestbranding=1&playsinline=1&enablejsapi=1&origin=${origin}`;
    }

    return `https://player.vimeo.com/video/${id}?title=0&byline=0&portrait=0`;
  }

  /**
   * @param {HTMLElement} container
   * @param {HTMLElement} thumb
   * @param {boolean} [instant]
   */
  function scrollThumbIntoView(container, thumb, instant) {
    if (!(container instanceof HTMLElement) || !(thumb instanceof HTMLElement)) return;
    if (container.scrollWidth <= container.clientWidth) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = instant || prefersReducedMotion ? 'auto' : 'smooth';

    thumb.scrollIntoView({
      behavior,
      inline: 'center',
      block: 'nearest',
    });
  }

  /**
   * @param {HTMLElement} container
   * @param {(thumb: HTMLElement) => void} onImageThumbActivate
   * @param {(thumb: HTMLElement) => void} onVideoThumbActivate
   */
  function setupThumbStripScroll(container, onImageThumbActivate, onVideoThumbActivate) {
    container.addEventListener(
      'wheel',
      (event) => {
        if (container.scrollWidth <= container.clientWidth) return;

        const absX = Math.abs(event.deltaX);
        const absY = Math.abs(event.deltaY);

        if (absX > 0 && absX >= absY) return;

        if (absY === 0) return;

        let scrollDelta = event.deltaY;

        if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) {
          scrollDelta *= 16;
        } else if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
          scrollDelta *= container.clientWidth;
        }

        container.scrollLeft += scrollDelta;
        event.preventDefault();
      },
      { passive: false }
    );

    /** @type {{ pointerId: number, startX: number, scrollStart: number, didDrag: boolean } | null} */
    let dragSession = null;
    let suppressClick = false;
    const dragThreshold = 10;

    /**
     * @param {Element | EventTarget | null} target
     * @returns {HTMLElement | null}
     */
    function getThumbFromTarget(target) {
      if (!(target instanceof Element)) return null;

      const thumb = target.closest('[data-gallery-thumb], [data-gallery-video-thumb]');
      return thumb instanceof HTMLElement ? thumb : null;
    }

    container.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      if (container.scrollWidth <= container.clientWidth) return;

      suppressClick = false;
      dragSession = {
        pointerId: event.pointerId,
        startX: event.clientX,
        scrollStart: container.scrollLeft,
        didDrag: false,
      };
    });

    container.addEventListener('pointermove', (event) => {
      if (!dragSession || event.pointerId !== dragSession.pointerId) return;

      const deltaX = event.clientX - dragSession.startX;

      if (!dragSession.didDrag) {
        if (Math.abs(deltaX) < dragThreshold) return;

        dragSession.didDrag = true;
        suppressClick = true;
        container.classList.add('is-dragging');
        container.setPointerCapture(event.pointerId);
      }

      container.scrollLeft = dragSession.scrollStart - deltaX;
    });

    /**
     * @param {PointerEvent} event
     */
    const endDragSession = (event) => {
      if (!dragSession || event.pointerId !== dragSession.pointerId) return;

      dragSession = null;
      container.classList.remove('is-dragging');

      if (container.hasPointerCapture(event.pointerId)) {
        container.releasePointerCapture(event.pointerId);
      }
    };

    container.addEventListener('pointerup', endDragSession);
    container.addEventListener('pointercancel', endDragSession);

    container.addEventListener('click', (event) => {
      if (suppressClick) {
        suppressClick = false;
        return;
      }

      const thumb = getThumbFromTarget(event.target);
      if (!(thumb instanceof HTMLElement)) return;

      if (thumb.hasAttribute('data-gallery-video-thumb')) {
        onVideoThumbActivate(thumb);
      } else {
        onImageThumbActivate(thumb);
      }
    });
  }

  /**
   * @param {HTMLElement} root
   */
  function initGallery(root) {
    if (!(root instanceof HTMLElement) || root.dataset.galleryInitialized === 'true') return;

    const mainFrame = root.querySelector('.custom-product-gallery__main-frame');
    const mainImageCandidate = mainFrame?.querySelector('.custom-product-gallery__image');
    const embedVideo = mainFrame?.querySelector('[data-gallery-embed-video]');
    const embedIframe =
      embedVideo instanceof HTMLElement ? embedVideo.querySelector('.custom-product-gallery__embed-iframe') : null;
    const thumbsContainer = root.querySelector('.custom-product-gallery__thumbs');
    const imageThumbs = root.querySelectorAll('[data-gallery-thumb]');
    const videoThumb = root.querySelector('[data-gallery-video-thumb]');
    const videoThumbImage = root.querySelector('[data-gallery-video-thumb-image]');

    if (!(mainFrame instanceof HTMLElement) || !(mainImageCandidate instanceof HTMLImageElement)) return;
    if (!imageThumbs.length && !(videoThumb instanceof HTMLElement)) return;

    /** @type {HTMLImageElement} */
    const mainImage = mainImageCandidate;

    const embedUrl = root.getAttribute('data-embed-video-url') || '';
    const embedData = parseEmbedVideoUrl(embedUrl);
    let embedSrc = '';

    if (embedData) {
      embedSrc = buildEmbedSrc(embedData.host, embedData.id);

      if (videoThumb instanceof HTMLElement) {
        videoThumb.hidden = false;
        videoThumb.dataset.embedSrc = embedSrc;

        if (videoThumbImage instanceof HTMLImageElement) {
          if (embedData.host === 'youtube') {
            setYouTubeThumbImage(videoThumbImage, embedData.id);
          } else {
            setVimeoThumbImage(videoThumbImage, embedData.id);
          }
        }
      }
    } else if (videoThumb instanceof HTMLElement) {
      videoThumb.remove();
    }

    root.dataset.galleryInitialized = 'true';

    const videoThumbs = root.querySelectorAll('[data-gallery-video-thumb]:not([hidden])');

    /**
     * @param {Element} item
     * @param {boolean} isActive
     */
    function setThumbState(item, isActive) {
      if (!(item instanceof HTMLElement)) return;

      item.classList.toggle('custom-product-gallery__thumb--active', isActive);
      item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    }

    function clearEmbedVideo() {
      if (!(embedVideo instanceof HTMLElement)) return;

      embedVideo.hidden = true;

      if (embedIframe instanceof HTMLIFrameElement) {
        embedIframe.src = '';
      }
    }

    function showMainImage() {
      mainImage.hidden = false;
      clearEmbedVideo();
    }

    /**
     * @param {HTMLElement} thumb
     */
    function setActiveImageThumb(thumb) {
      const fullSrc = thumb.getAttribute('data-full-src');
      const fullSrcset = thumb.getAttribute('data-full-srcset');
      const label = thumb.getAttribute('aria-label');

      if (!fullSrc) return;

      showMainImage();
      mainImage.src = fullSrc;
      mainImage.srcset = fullSrcset || '';

      if (label) {
        mainImage.alt = label;
      }

      imageThumbs.forEach((item) => {
        setThumbState(item, item === thumb);
      });

      videoThumbs.forEach((item) => {
        setThumbState(item, false);
      });

      if (thumbsContainer instanceof HTMLElement) {
        scrollThumbIntoView(thumbsContainer, thumb);
      }
    }

    /**
     * @param {HTMLElement} thumb
     */
    function setActiveVideoThumb(thumb) {
      const activeEmbedSrc = thumb.dataset.embedSrc || embedSrc;

      if (!activeEmbedSrc || !(embedVideo instanceof HTMLElement) || !(embedIframe instanceof HTMLIFrameElement)) {
        return;
      }

      mainImage.hidden = true;
      embedVideo.hidden = false;

      const separator = activeEmbedSrc.includes('?') ? '&' : '?';
      embedIframe.src = `${activeEmbedSrc}${separator}autoplay=1`;

      imageThumbs.forEach((item) => {
        setThumbState(item, false);
      });

      videoThumbs.forEach((item) => {
        setThumbState(item, item === thumb);
      });

      if (thumbsContainer instanceof HTMLElement) {
        scrollThumbIntoView(thumbsContainer, thumb);
      }
    }

    if (thumbsContainer instanceof HTMLElement) {
      setupThumbStripScroll(thumbsContainer, setActiveImageThumb, setActiveVideoThumb);

      const initialActiveThumb = root.querySelector(
        '.custom-product-gallery__thumb--active, .custom-product-gallery__thumb[aria-pressed="true"]'
      );

      if (initialActiveThumb instanceof HTMLElement) {
        scrollThumbIntoView(thumbsContainer, initialActiveThumb, true);
      }
    }
  }

  function initAll() {
    document.querySelectorAll('[data-custom-product-gallery]').forEach((gallery) => {
      if (gallery instanceof HTMLElement) {
        initGallery(gallery);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    target.querySelectorAll('[data-custom-product-gallery]').forEach((gallery) => {
      if (!(gallery instanceof HTMLElement)) return;

      delete gallery.dataset.galleryInitialized;
      initGallery(gallery);
    });
  });
})();
